// ConsoleApp - the authenticated console UI (fully wired).
// G360_K495 - Agent Map has no menu entry of its own; it is a card on Agents &
// Models. The 'agent-map' route stays, and forwards there with the map open.

import { lazy, Suspense, useEffect, useState } from 'react';
// G360_K623 - each page is its own chunk, loaded when opened. One bundle of every page
// outgrew the build's memory (Netlify exit 134) and made first load slower.
import { LayoutDashboard, Radar, Bot, AlertTriangle, Activity, SlidersHorizontal, Shield, Flag, ClipboardCheck, ListChecks, Users, Plug, FileSearch, Palette, CreditCard, ShieldCheck, Gauge, ShieldAlert, Building2, Webhook, Rocket, ScanLine, Database, FileText, HelpCircle, Download, ShieldHalf, User, Route, FileWarning, Newspaper } from 'lucide-react';
import { AppShell, supabase, useAuth } from '@govern360/shared';
const DashboardPage = lazy(() => import('./pages/DashboardPage.jsx'));
const InventoryPage = lazy(() => import('./pages/InventoryPage.jsx'));
const DiscoveryPage = lazy(() => import('./pages/DiscoveryPage.jsx'));
const AgentsPage = lazy(() => import('./pages/AgentsPage.jsx'));
const NhiRegistryPage = lazy(() => import('./pages/NhiRegistryPage.jsx'));
const SupervisionHealthPage = lazy(() => import('./pages/SupervisionHealthPage.jsx'));
const ExposureScorePage = lazy(() => import('./pages/ExposureScorePage.jsx'));
const ExposureMethodologyPage = lazy(() => import('./pages/ExposureMethodologyPage.jsx'));
const McpGatewayPage = lazy(() => import('./pages/McpGatewayPage.jsx'));
const RisksPage = lazy(() => import('./pages/RisksPage.jsx'));
const PoliciesPage = lazy(() => import('./pages/PoliciesPage.jsx'));
const DataShieldPage = lazy(() => import('./pages/DataShieldPage.jsx'));
const IncidentsPage = lazy(() => import('./pages/IncidentsPage.jsx'));
const ComplianceEnforcementPage = lazy(() => import('./pages/ComplianceEnforcementPage.jsx')); // G360_K575 - Compliance | EU AI Act | Enforcement
const UsersPage = lazy(() => import('./pages/UsersPage.jsx'));
const AuditPage = lazy(() => import('./pages/AuditPage.jsx'));
const BillingPage = lazy(() => import('./pages/BillingPage.jsx'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage.jsx'));
const CloudAccountsPage = lazy(() => import('./pages/CloudAccountsPage.jsx'));
const BrandingPage = lazy(() => import('./pages/BrandingPage.jsx'));
const PostureScorePage = lazy(() => import('./pages/PostureScorePage.jsx'));
const CoveragePage = lazy(() => import('./pages/CoveragePage.jsx'));
const VendorRiskPage = lazy(() => import('./pages/VendorRiskPage.jsx'));
const AccessRequestsPage = lazy(() => import('./pages/AccessRequestsPage.jsx'));
const RoutingRulesPage = lazy(() => import('./pages/RoutingRulesPage.jsx'));
const SiemSettingsPage = lazy(() => import('./pages/SiemSettingsPage.jsx'));
const DeployPage = lazy(() => import('./pages/DeployPage.jsx'));
const ResponseScanPage = lazy(() => import('./pages/ResponseScanPage.jsx'));
const ReportsHubPage = lazy(() => import('./pages/ReportsHubPage.jsx')); // G360_K575 - General Reports | Exposure Report
const HelpSupportPage = lazy(() => import('./pages/HelpSupportPage.jsx'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage.jsx'));
const FaqPage = lazy(() => import('./pages/FaqPage.jsx'));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage.jsx'));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage.jsx'));
const ArchitectureReviewPage = lazy(() => import('./pages/ArchitectureReviewPage.jsx'));
const BehavioralRiskPage = lazy(() => import('./pages/BehavioralRiskPage.jsx'));
const CaioProgramPage = lazy(() => import('./pages/CaioProgramPage.jsx'));
const CfoSpendPage = lazy(() => import('./pages/CfoSpendPage.jsx'));
const CfoValuePage = lazy(() => import('./pages/CfoValuePage.jsx'));
const CroRiskPage = lazy(() => import('./pages/CroRiskPage.jsx'));
const CisoExposurePage = lazy(() => import('./pages/CisoExposurePage.jsx'));
const CioEstatePage = lazy(() => import('./pages/CioEstatePage.jsx'));
const BoardPage = lazy(() => import('./pages/BoardPage.jsx'));
const ExposurePathsPage = lazy(() => import('./pages/ExposurePathsPage.jsx'));
const OwaspTop10Page = lazy(() => import('./pages/OwaspTop10Page.jsx')); // G360_K557 - one OWASP page, three tabs
const MyAgentsPage = lazy(() => import('./pages/MyAgentsPage.jsx'));
const PurviewPage = lazy(() => import('./pages/PurviewPage.jsx'));
const TokenGovernancePage = lazy(() => import('./pages/TokenGovernancePage.jsx'));
const CopilotConsumptionPage = lazy(() => import('./pages/CopilotConsumptionPage.jsx'));
const CopilotPage = lazy(() => import('./pages/CopilotPage.jsx'));
import { Coins, Sparkles, Scale } from 'lucide-react';
const DataPosturePage = lazy(() => import('./pages/DataPosturePage.jsx'));
const SecuritySettingsPage = lazy(() => import('./pages/SecuritySettingsPage.jsx'));
const GovernanceRulesPage = lazy(() => import('./pages/GovernanceRulesPage.jsx'));
const DecisionsPage = lazy(() => import('./pages/DecisionsPage.jsx'));
const ZeroTrustPage = lazy(() => import('./pages/ZeroTrustPage.jsx'));
const PurposeRegisterPage = lazy(() => import('./pages/PurposeRegisterPage.jsx'));
const StartHerePage = lazy(() => import('./pages/StartHerePage.jsx'));
// G360_K632 - Release Certification (phase 1, record only)
const ReleaseCertificationPage = lazy(() => import('./pages/ReleaseCertificationPage.jsx'));
import { BadgeCheck } from 'lucide-react';
// G360_K647 - Network Block List (Palo Alto External Dynamic Lists)
const NetworkBlockListPage = lazy(() => import('./pages/NetworkBlockListPage.jsx'));
import { Ban } from 'lucide-react';
import ConsentBanner from './components/ConsentBanner.jsx';
import EntraConsentBanner from './components/EntraConsentBanner.jsx';
// G360_K634 - one-time Fabric AI consumption notice for owners and admins
import FabricNotice from './components/FabricNotice.jsx';
import SessionTimeout from './components/SessionTimeout.jsx';
import OrgSwitcher from './components/OrgSwitcher.jsx';
import CommandPalette from './components/CommandPalette.jsx';
import AIAssistant from './components/AIAssistant.jsx';
const AgentDossierPage = lazy(() => import('./pages/AgentDossierPage.jsx'));
import OnboardingWizard from './components/OnboardingWizard.jsx';
import { getOnboardingStatus, shouldShowWizard } from './lib/onboardingApi.js';

/* G360_K418 - the Governance Register and the Agent Trust Dossier are
   one route, not two.

   A separate route would need an entry in every role scope, and a role
   that missed it would land silently on a fallback page - a permission
   to keep in sync forever, for a detail view of a page the person can
   already see. The agent id rides in the hash query instead, which
   readHashQuery already exposes, so a dossier URL can be pasted into a
   review and reopened.

   A wrapper rather than an edit inside DecisionsPage: that file is
   46 KB and changing its signature on partial sight is how a working
   page breaks. This needs nothing from it but its name. */
function GovernanceRegisterRoute(props) {
  const agent = props.params ? props.params.get('agent') : null;
  if (agent) {
    return (
      <AgentDossierPage
        agentId={agent}
        tab={props.params ? props.params.get('tab') : null}
        navigate={props.navigate}
      />
    );
  }
  return <DecisionsPage {...props} />;
}

/* G360_K495 - old links, bookmarks and saved role scopes that name
   'agent-map' land on Agents & Models with the Agent Map card open.
   replace(), so Back does not bounce through the forward. */
function AgentMapRedirect() {
  useEffect(() => { window.location.replace('#agents?map=families'); }, []);
  return null;
}

const PAGES = {
  dashboard: DashboardPage,
  posture: PostureScorePage,
  inventory: InventoryPage,
  discovery: DiscoveryPage,
  agents: AgentsPage,
  nhi: NhiRegistryPage,
  supervision: SupervisionHealthPage,
  exposure: ReportsHubPage,
  'exposure-score': ExposureScorePage,
  'exposure-methodology': ExposureMethodologyPage,
  mcpgw: McpGatewayPage,
  risks: RisksPage,
  policies: PoliciesPage,
  datashield: DataShieldPage,
  responsescan: ResponseScanPage,
  incidents: IncidentsPage,
  compliance: ComplianceEnforcementPage,
  'eu-ai-act': ComplianceEnforcementPage,
  enforcement: ComplianceEnforcementPage,
  coverage: CoveragePage,
  vendors: VendorRiskPage,
  'access-requests': AccessRequestsPage,
  'routing-rules': RoutingRulesPage,
  purview: PurviewPage,
  dataposture: DataPosturePage,
  'token-governance': TokenGovernancePage,
  'copilot-consumption': CopilotConsumptionPage,
  'purpose-register': PurposeRegisterPage,
  'start-here': StartHerePage,
  'g360-copilot': CopilotPage,
  'architecture-review': ArchitectureReviewPage,
  'behavioral-risk': BehavioralRiskPage,
  'exec-caio': CaioProgramPage,
  'exec-cfo': CfoSpendPage,
  'exec-cfo-value': CfoValuePage,
  'exec-cro': CroRiskPage,
  'exec-ciso': CisoExposurePage,
  'exec-cio': CioEstatePage,
  'exec-board': BoardPage,
  'agent-map': AgentMapRedirect,
  'exposure-paths': ExposurePathsPage,
  'owasp-top10': OwaspTop10Page,
  'asi-coverage': OwaspTop10Page,
  'my-agents': MyAgentsPage,
  'llm-top10': OwaspTop10Page,
  users: UsersPage,
  security: SecuritySettingsPage,
  'review-rules': GovernanceRulesPage,
  'governance-register': GovernanceRegisterRoute,
  // G360_K632
  'release-certification': ReleaseCertificationPage,
  // G360_K647
  'network-block': NetworkBlockListPage,
  'zero-trust': ZeroTrustPage,
  integrations: IntegrationsPage,
  connections: CloudAccountsPage,
  siem: SiemSettingsPage,
  deploy: DeployPage,
  deploypolicies: DeployPage, // G360_K577 - the Deploy Policies tab of Endpoint Governance
  reports: ReportsHubPage,
  help: HelpSupportPage,
  documentation: DocumentationPage,
  faq: FaqPage,
  support: SupportTicketPage,
  audit: AuditPage,
  branding: BrandingPage,
  billing: BillingPage,
  account: MyAccountPage,
};

function readHash() {
  const h = (window.location.hash || '#dashboard').slice(1);
  return h.split('?')[0] || 'exposure-score';
}

/* G360_K416 - the half readHash throws away.
   readHash has always split on '?' and kept only the route, so
   #governance-register?agent=<uuid> already lands on the right page -
   the parameters were simply discarded. This returns them, so a page
   can be opened on a specific record and that URL can be pasted into
   a review or an evidence pack.
   A hash with no '?' returns an empty string, which is what every
   page has effectively been given until now. */
function readHashQuery() {
  const h = (window.location.hash || '').slice(1);
  const q = h.indexOf('?');
  return q >= 0 ? h.slice(q + 1) : '';
}

export default function ConsoleApp() {
  const [route, setRoute] = useState(readHash());
  const [hashQuery, setHashQuery] = useState(readHashQuery());
  /* G360_ROLE_SCOPE - a tenant-defined role may limit which pages the
     console shows. null means unrestricted, which is what every
     built-in role returns, so existing users are unaffected.

     This scopes the CONSOLE, not the data. RLS still decides what the
     API will return - do not treat this as an access control. */
  const { tenant } = useAuth();
  const [allowedRoutes, setAllowedRoutes] = useState(null);
  const [toolCount, setToolCount] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);

  useEffect(() => {
    const onHash = () => { setRoute(readHash()); setHashQuery(readHashQuery()); };
    window.addEventListener('hashchange', onHash);
    if (!window.location.hash) window.location.hash = '#dashboard';
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    let active = true;
    getOnboardingStatus().then((status) => {
      if (!active) return;
      setWizard(shouldShowWizard(status));
      setStatusChecked(true);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    supabase.from('ai_tools').select('id', { count: 'exact', head: true })
      .then(({ count }) => { if (active) setToolCount(count ?? 0); });
    return () => { active = false; };
  }, [route, wizard]);

  /* G360_K121 - the sidebar reads as the governance lifecycle rather
     than as feature buckets. Visibility held 13 items and Control 14;
     Control had become anything that was not a report. Eight groups,
     none of the workflow groups over six items, utilities last.

     Discover what AI exists. Assess what creates exposure. Watch the
     cost. Govern accountability. Enforce on planes you own. Prove it. */
  const NAV_GROUPS = [
    {
      // G360_EXEC - Executive Intelligence. Role-shaped summaries inside the
      // existing shell, first in the nav because an executive who has to hunt
      // for a view does not use it. Each answers ONE question from sourced
      // figures, states its own measurement coverage, and shows no
      // illustrative number. Board, CIO, CISO, CFO and CRO follow here.
      label: 'Executive Intelligence', icon: Gauge,
      items: [
        // g360-k331 - moved here from Assess. Six pillar stages against
        // CISA ZTMM v2.0 with a benchmark is a board artifact, not an
        // operator tool; the tasks beneath it link out to the pages where
        // the work is actually done. Above Board because Board summarises
        // findings and this is the frame they sit inside.
        { id: 'zerotrust', label: 'Zero Trust', icon: Shield, route: 'zero-trust' },
        { id: 'execboard', label: 'Board', icon: ClipboardCheck, route: 'exec-board' },
        { id: 'execcaio', label: 'CAIO', icon: ClipboardCheck, route: 'exec-caio' },
        { id: 'execcio', label: 'CIO', icon: ClipboardCheck, route: 'exec-cio' },
        { id: 'execciso', label: 'CISO', icon: ClipboardCheck, route: 'exec-ciso' },
        { id: 'execcfo', label: 'CFO - Spend', icon: ClipboardCheck, route: 'exec-cfo' },
        { id: 'execcfoval', label: 'CFO - Value', icon: ClipboardCheck, route: 'exec-cfo-value' },
        { id: 'execcro', label: 'CRO', icon: ClipboardCheck, route: 'exec-cro' },
      ],
    },
    {
      label: 'Home', icon: LayoutDashboard,
      items: [
        { id: 'exposurescore', label: 'AI Exposure Score', icon: ShieldCheck, route: 'exposure-score' },
        // The ranked decision queue. An input, not an artefact - it was
        // filed under Reports, where nobody who had collapsed that group
        // ever saw it appear.
        { id: 'starthere',    label: 'Decisions',         icon: ListChecks,     route: 'start-here' },
        { id: 'dashboard',    label: 'Dashboard',         icon: LayoutDashboard, route: 'dashboard' },
        // G360_K511 - opens news.govern360.ai in a new tab (see navigate).
        { id: 'ainews',       label: 'Latest AI News',    icon: Newspaper,       route: 'ainews' },
      ],
    },
    {
      label: 'Discover', icon: Radar,
      items: [
        { id: 'inventory', label: 'AI Inventory',    icon: Radar,       route: 'inventory', badge: toolCount ?? undefined },
        // G360_K124 - "Discovered AI", not "Shadow AI": that term is already
        // a status on inventoried tools and a scored posture driver, and this
        // page holds untriaged candidates, which is a different population.
        // G360_K127 - the page is coverage, scans and a triage queue, not a
        // list of findings. "Discovered AI" promised findings and rendered
        // zero of them. This names the activity instead.
        { id: 'discovery', label: 'AI Discovery',   icon: Radar,       route: 'discovery' },
        { id: 'agents',    label: 'Agents & Models', icon: Bot,         route: 'agents' },
        { id: 'nhi',       label: 'Agent Identity (NHI)', icon: ShieldCheck, route: 'nhi' },
      ],
    },
    {
      label: 'Assess', icon: ShieldAlert,
      items: [
        { id: 'exposurepaths', label: 'Exposure Paths', icon: AlertTriangle, route: 'exposure-paths' },
        { id: 'govregister',  label: 'Governance Register', icon: ClipboardCheck, route: 'governance-register' },
        // G360_K632 - signed, time-bound release approval per agent; records only in phase 1.
        { id: 'releasecert',  label: 'Release Certification', icon: BadgeCheck, route: 'release-certification' },
        // Risk Register, not AI Risk Registry - every risk here is
        // AI-related, and the short form is what audit teams say.
        { id: 'risks',      label: 'Risk Register',  icon: AlertTriangle, route: 'risks' },
        { id: 'owasp',      label: 'OWASP Top 10',   icon: ClipboardCheck, route: 'owasp-top10', also: ['asi-coverage', 'llm-top10'] },
        { id: 'myagents',   label: 'Your AI agents', icon: ClipboardCheck, route: 'my-agents' },
        { id: 'behrisk',    label: 'Behavioral Risk', icon: Activity,     route: 'behavioral-risk' },
        { id: 'dataposture', label: 'Data Posture',   icon: Database,     route: 'dataposture' },
        { id: 'vendors',    label: 'Vendor AI Risk',  icon: ShieldAlert,  route: 'vendors' },
      ],
    },
    {
      label: 'Cost', icon: Coins,
      items: [
        { id: 'tokengov',      label: 'Token Governance',    icon: Coins,    route: 'token-governance' },
        { id: 'copilotconsum', label: 'Copilot Consumption', icon: Sparkles, route: 'copilot-consumption' },
      ],
    },
    {
      label: 'Govern', icon: Scale,
      items: [
        { id: 'purposereg',   label: 'Purpose Register',     icon: ClipboardCheck, route: 'purpose-register' },
        { id: 'archreview',   label: 'Architecture Review',  icon: ShieldCheck,    route: 'architecture-review' },
        { id: 'supervision',  label: 'Supervision Health',   icon: Activity,       route: 'supervision' },
        { id: 'accessreq',    label: 'Access Requests',      icon: ClipboardCheck, route: 'access-requests' },
        { id: 'routingrules', label: 'Approval Routing',     icon: Route,          route: 'routing-rules' },
        // Incidents are worked - assigned, actioned, closed - not browsed.
        { id: 'incidents',    label: 'Incidents',            icon: Flag,           route: 'incidents' },
      ],
    },
    {
      label: 'Enforce', icon: Shield,
      items: [
        { id: 'policies',       label: 'Policies',       icon: SlidersHorizontal, route: 'policies' },
        { id: 'datashield',     label: 'Data Shield',    icon: Shield,            route: 'datashield' },
        { id: 'mcpgw',          label: 'MCP Gateway',    icon: ShieldHalf,        route: 'mcpgw' },
        // G360_K647 - what Palo Alto firewalls and Prisma Access block, as External Dynamic Lists
        { id: 'netblock',       label: 'Network Block List', icon: Ban,           route: 'network-block' },
        // The plane the compiled artefact lands on, and where read-back
        // confirms it. Someone asking whether a policy is actually
        // enforcing looks here, not under an integrations list.
        { id: 'purview',        label: 'Purview',        icon: Building2,         route: 'purview' },
        // G360_K577 - Deploy Policies is a tab of Endpoint Governance.
        { id: 'deploy',         label: 'Endpoint Governance', icon: Download,     route: 'deploy', also: ['deploypolicies'] },
        // G360_K122 - a tool x plane matrix: enforcing (live) vs compile
        // (recommendation) vs cannot-reach vs none. It reads across
        // everything above it, so it sits last. Named "Enforcement"
        // because "Coverage" alone does not say coverage of what.
        { id: 'coverage',       label: 'Enforcement Coverage', icon: ShieldCheck,   route: 'coverage' },
      ],
    },
    {
      label: 'Prove', icon: BadgeCheck,
      items: [
        // G360_K575 - one entry per tabbed page; 'also' lists the routes of its
        // other tabs, so the entry is highlighted on any of them and a role
        // that may open only one of them still gets the entry.
        { id: 'compliance', label: 'Compliance & Enforcement', icon: ClipboardCheck, route: 'compliance', also: ['eu-ai-act', 'enforcement'] },
        { id: 'reports',    label: 'Reports',        icon: FileText,       route: 'reports', also: ['exposure'] },
        { id: 'audit',      label: 'Audit Log',      icon: FileSearch,     route: 'audit' },
      ],
    },
    {
      label: 'Admin', icon: SlidersHorizontal,
      items: [
        { id: 'integrations', label: 'Integrations',   icon: Plug,        route: 'integrations' },
        { id: 'connections',  label: 'Connections',    icon: Plug,        route: 'connections' },
        { id: 'siem',         label: 'SIEM Streaming', icon: Webhook,     route: 'siem' },
        { id: 'users',        label: 'Users & Roles',  icon: Users,       route: 'users' },
        { id: 'security',     label: 'Security Settings', icon: ShieldCheck, route: 'security' },
        { id: 'govrules',     label: 'Alert Rules', icon: ShieldCheck, route: 'review-rules' }, // G360_K366 - renamed; nobody hunting for their alerts would look under the old label
        { id: 'g360copilot',  label: 'G360 Copilot',   icon: Bot,         route: 'g360-copilot' },
        { id: 'branding',     label: 'Branding',       icon: Palette,     route: 'branding' },
        { id: 'billing',      label: 'Billing',        icon: CreditCard,  route: 'billing' },
        { id: 'implguide',    label: 'Implementation Guide', icon: FileText, route: 'implguide' },
        { id: 'adminguide',   label: 'Administrator Guide', icon: FileText, route: 'adminguide' }, // G360_K685 - every console page, in detail
        { id: 'help',         label: 'Help & Support', icon: HelpCircle,  route: 'help' },
        { id: 'account',      label: 'My Account',     icon: User,        route: 'account' },
      ],
    },
  ];
  // RETIRED: superseded by the AI Exposure Score. Route still registered,
  // reachable by URL. { id: 'posture', label: 'AI Posture', icon: Gauge, route: 'posture' },
  // HIDDEN until the extension response-hook exists (no inbound data yet):
  // { id: 'responsescan', label: 'Response Scan', icon: ScanLine, route: 'responsescan' },

  /* G360_ROLE_SCOPE - fetch once per tenant. On any failure the scope
     stays null (unrestricted) rather than locking the user out of
     their own console because a query failed. */
  useEffect(() => {
    let active = true;
    if (!tenant?.id) return undefined;
    supabase.rpc('my_role_scope', { p_tenant: tenant.id }).then(({ data }) => {
      if (!active) return;
      const row = Array.isArray(data) ? data[0] : null;
      const list = row && row.allowed_routes;
      setAllowedRoutes(Array.isArray(list) && list.length ? list : null);
    }, () => { if (active) setAllowedRoutes(null); });
    return () => { active = false; };
  }, [tenant?.id]);

  /* Always reachable regardless of scope: a person who cannot open
     their own account page or ask for help has been locked out of the
     product, not scoped within it. */
  const ALWAYS = ['account', 'help', 'support', 'faq', 'ainews']; // G360_K511 - a public news site, not a page
  /* G360_K557 - a role that may open either OWASP list may open the merged page. */
  const OWASP_ROUTES = ['owasp-top10', 'asi-coverage', 'llm-top10'];
  /* G360_K575 - the Enforcement tab was part of the Compliance page, so a
     role that may open Compliance may open it. */
  const INHERITS = { enforcement: ['compliance'] };
  /* G360_K632 - a role scoped to the Governance Register may open Release
     Certification, which certifies the same agents; saved scopes predate it. */
  INHERITS['release-certification'] = ['governance-register'];
  /* G360_K647 - a role scoped to Policies may open the Network Block List; saved scopes predate it. */
  INHERITS['network-block'] = ['policies'];
  const routeAllowed = (r) => !allowedRoutes || ALWAYS.includes(r) || allowedRoutes.includes(r) || (OWASP_ROUTES.includes(r) && OWASP_ROUTES.some((x) => allowedRoutes.includes(x)))
    || (INHERITS[r] || []).some((x) => allowedRoutes.includes(x));

  /* G360_K575 - an entry whose main route the role may not open points at
     the first of its other tabs the role may open. */
  const VISIBLE_GROUPS = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.map((it) => (routeAllowed(it.route) || !it.also ? it : { ...it, route: it.also.find(routeAllowed) || it.route })).filter((it) => routeAllowed(it.route)) }))
    .filter((g) => g.items.length > 0);

  function navigate(r) {
    if (r === 'implguide') { window.open('/implementation-guide.html', '_blank', 'noopener,noreferrer'); return; }
    if (r === 'adminguide') { window.open('/admin-guide.html', '_blank', 'noopener,noreferrer'); return; } // G360_K685
    if (r === 'ainews') { window.open('https://news.govern360.ai', '_blank', 'noopener,noreferrer'); return; } // G360_K511
    window.location.hash = `#${r}`;
  }
  /* G360_ROLE_SCOPE - a filtered nav is not a gate; the hash can be
     typed. An out-of-scope route falls back to the first page the role
     CAN see rather than rendering the page or showing a dead screen. */
  const fallbackRoute = allowedRoutes ? (allowedRoutes[0] || 'account') : 'dashboard';
  const effectiveRoute = routeAllowed(route) ? route : fallbackRoute;
  const PageComponent = PAGES[effectiveRoute] || DashboardPage;
  /* G360_K575 - the sidebar entry to highlight: the one whose route, or one of
     whose tab routes, is on screen. */
  const navItem = VISIBLE_GROUPS.reduce((a, g) => a.concat(g.items), []).find((it) => it.route === effectiveRoute || (it.also || []).includes(effectiveRoute));
  const navRoute = navItem ? navItem.route : effectiveRoute;
  /* G360_TRUST_CHIP - was a green check-shield reading "SOC 2 - ISO
     42001", which at a glance is a certification claim. SOC 2 is
     architected with the audit in progress, and no status has ever been
     claimed for ISO 42001 - that is a framework the product maps
     controls against, which belongs on the Compliance page where it is
     explained, not in a badge above a customer tenant. */
  // G360_TRUST_LINK - the chip was a span, so the click fell through
  // to a parent that routes to the dashboard. Six words cannot carry
  // the whole position: which certified platforms this runs on, who
  // the subprocessors are, what leaves the tenant. The trust page can,
  // so the chip points at it.
  //
  // "Trust centre" rather than "Trusted platform" - naming a
  // destination is not a claim, and it is the term a reviewer knows.
  // The SOC 2 wording lives on the page, where there is room for it.
  //
  // Relative url: the page is served from this console's own public/,
  // not the marketing site. stopPropagation because the parent routes
  // on click.
  const topBar = (
    <a className="ap-chip"
      href="/trust.html" target="_blank" rel="noopener"
      onClick={(e) => e.stopPropagation()}
      style={{ background: 'var(--g-bg-soft)', color: 'var(--g-text-2)', textDecoration: 'none' }}
      title="Subprocessors, the certified platforms Govern360 runs on, and what leaves your tenant. Govern360 is built to SOC 2 controls with the audit in progress - not yet certified.">
      <ShieldHalf size={14} />Trust centre
    </a>
  );

  return (
    <>
      {wizard && statusChecked && (
        <OnboardingWizard onClose={() => { setWizard(false); navigate('dashboard'); }} />
      )}
      <AppShell navGroups={VISIBLE_GROUPS} currentRoute={navRoute} onNavigate={navigate} topBar={topBar} sidebarTop={<OrgSwitcher />} sidebarFoot={<AIAssistant navGroups={VISIBLE_GROUPS} onNavigate={navigate} />} searchSlot={<CommandPalette navigate={navigate} navGroups={VISIBLE_GROUPS} />}/* G360_K886 - the header box IS the search; K715 menu search kept inside it */ onBellClick={() => navigate('risks')} onHelpClick={() => navigate('help')}>
        <ConsentBanner />
        <EntraConsentBanner />
        {/* G360_K634 */}
        <FabricNotice />
        {/* G360_K623 - pages load on demand; this shows while one arrives */}
        <Suspense fallback={<div data-testid="page-loading" style={{ padding: 24, fontSize: 13, color: 'var(--g-text-3)' }}>Loading...</div>}>
          <PageComponent route={effectiveRoute} navigate={navigate} params={new URLSearchParams(hashQuery)} canRoute={routeAllowed} />
        </Suspense>
        <SessionTimeout />
      </AppShell>
      {/* G360_K360 - the assistant now renders inside the sidebar footer. */}
    </>
  );
}
